import { useState, useEffect } from 'react'

import { AutoCompleteInput, Button, Grid, Select, Span, TableFooter } from 'components'
import { DeleteIcon } from 'components/Icons'
import { useModal, useLoading, useDebounce, id } from 'hooks'
import { ParamCockpitServices } from 'services/NGT/Parameterization'
import { UserCsnService } from 'services/Supplier/Users'
import { SPACING, TYPOGRAPHY } from 'utils'

import {
  TableContainerStyled,
  TableStyled,
  TableHeadStyled,
  TableRowStyled,
  TableCellStyled,
  TableBodyStyled
} from './RegistrationTable.styles'

interface IAutoCompleteEntity {
  idUsuario: number
  nmUsuario: string
  idUsuarioCsn: number
  cdMatricula: string
}

export const RegistrationTable = () => {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(15)
  const [matriculasCsn, setMatriculasCsn] = useState<
    {
      label: string
      value: any
    }[]>([])
  const [matriculasList, setMatriculasList] = useState<any[]>([])
  const { setLoading } = useLoading()
  const { showModal } = useModal()
  const [selectedMatricula, setSelectedMatricula] = useState('')
  const [selectIdMatricula, setSelectIdMatricula] = useState(0)

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const getMatriculasCsn = (e: any) => {
    setLoading('loading')
    UserCsnService()
      .autoComplete({ cdMatricula: e.target.value, isNGT: true, hasLikeSearch: e.target.value.includes('%') })
      .then(({ data }) => {
        setMatriculasCsn(data.entity.map((analista) => ({
          label: analista.cdMatricula,
          value: analista.idUsuarioCsn
        })))
        setLoading('idle')
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível carregar a lista de Matrículas',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
  }

  const getMatriculasList = () => {
    setLoading('loading')
    ParamCockpitServices()
      .getMatriculas()
      .then(({ data }) => {
        setMatriculasList(data)
        setLoading('idle')
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível carregar as Matrículas cadastradas',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
  }

  const handleChangeMatricula = (value, idValue) => {
    setSelectIdMatricula(idValue)
    setSelectedMatricula(value)
  }

  const addMatricula = () => {
    if (matriculasList?.find((el) => el.idUsuarioCsn === selectIdMatricula)) {
      showModal({
        hideButtonCancel: true,
        message: 'Esta Matrícula já foi incluída anteriormente.',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })
    } else {
      setLoading('loading')
      ParamCockpitServices()
        .createMatriculas({ usuarioCsnId: selectIdMatricula })
        .then(() => {
          getMatriculasList()
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message: 'Matrícula incluída com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })
          setSelectedMatricula('')
        })
        .catch((error) => {
          showModal({
            hideButtonCancel: true,
            message: 'Erro ao incluir a Matrícula, tente novamente!',
            txtButtonConfirm: 'OK',
            type: 'error'
          })
          setLoading('idle')
        })
    }
  }

  const handleDelete = (idCockpitMatricula: number) => {
    setLoading('loading')
    ParamCockpitServices()
      .deleteMatriculas(idCockpitMatricula)
      .then((response) => {
        if (response.status === 200) {
          showModal({
            hideButtonCancel: true,
            message: 'Excluído com sucesso!',
            txtButtonConfirm: 'OK',
            type: 'success'
          })
          getMatriculasList()
        }
      })
      .catch((error) => {
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao excluir a Matrícula, tente novamente!',
          txtButtonConfirm: 'OK',
          type: 'error'
        })
        setLoading('error')
      })
      .finally(() => {
        setLoading('idle')
      })
  }

  useEffect(() => {
    getMatriculasList()
  }, [])

  return (
    <>
      <Grid
        gridCols="3.5fr 0.4fr 4fr"
        gridGap={`0 ${SPACING.spacing20}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing30} 0`}
      >
        <AutoCompleteInput
          name="nomeAnalista"
          marginTop={SPACING.spacing5}
          onChangeText={useDebounce(getMatriculasCsn, 600)}
          options={matriculasCsn || []}
          optionValue="label"
          value={selectedMatricula || ''}
          label="Nome Analista"
          noFilter
          onChange={(value, idValue) => {
            handleChangeMatricula(value, idValue)
          }}
          valueName="value"
        />
        <Button text="+" onClick={addMatricula} />
      </Grid>

      <Span
        text="Para cálculo de risco na visão Cockpit, desconsiderar dados abaixo:"
        fontSize={TYPOGRAPHY.bodySize16}
      />
      <Grid
        gridCols="5fr 3fr"
        gridGap={`0 ${SPACING.spacing10}`}
        margin={`${SPACING.spacing20} 0 ${SPACING.spacing30} 0`}
      >
        <TableContainerStyled>
          <TableStyled stickyHeader>
            <TableHeadStyled>
              <TableRowStyled>
                <TableCellStyled minwidth="18em">Matrícula</TableCellStyled>
                <TableCellStyled>Ação</TableCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            <TableBodyStyled>
              {(rowsPerPage > 0
                ? matriculasList?.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
                : matriculasList
              )?.map((row) => (
                <TableRowStyled key={row.idCockpitMatricula}>
                  <TableCellStyled>{row.nmMatricula}</TableCellStyled>
                  <TableCellStyled>
                    <DeleteIcon
                      onClick={() =>
                        showModal({
                          handleConfirm: () =>
                            handleDelete(row.idCockpitMatricula),
                          message: 'Confirma exclusão (S/N)?',
                          txtButtonCancel: 'NÃO',
                          txtButtonConfirm: 'SIM',
                          type: 'alert'
                        })
                      }
                      cursor="pointer"
                    />
                  </TableCellStyled>
                </TableRowStyled>
              ))}
            </TableBodyStyled>
          </TableStyled>

          <TableFooter
            page={page}
            rows={matriculasList}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            typeFooter="offline"
          />
        </TableContainerStyled>
      </Grid>
    </>
  )
}
